import { LoaderFunctionArgs } from '@remix-run/node';
import { HeaderProps, CustomerProfile, KnowledgeHubBookContextProps } from '@models/ui';
import { useLoaderData, useNavigate } from '@remix-run/react';
import './knowledge-hub-book.css';
import { useEffect, useState, useRef } from 'react';
import { DesktopComponent, MobileComponent } from './components';
import { KhBookContext } from './kh-book-context';
import { getApiData, getApiError, getCommonErrorMessage, HFWApiError, ResourceCommentViewModel, ResourceLikesInputModel, ResourceLikesViewModel } from '@models/api';
import { BrowserCustomerClient } from '@client';
import { NotFound404 } from '@components';
import { mixpanelService } from '@utilities';

export default function KnowledgeBase() {
  const resp = useLoaderData<typeof loader>();
  const data = resp.data.data;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isCommentsPopupOpen, setIsCommentsPopupOpen] = useState<boolean>(false);
  const [isSummaryPopupOpen, setIsSummaryPopupOpen] = useState<boolean>(false);
  const [isReactionsPop, setIsReactionsPop] = useState<boolean>(false);
  const [isPopupLinkVisible, setIsPopupLinkVisible] = useState<boolean>(false);
  const [likes, setLikes] = useState<ResourceLikesViewModel>(resp?.likes);
  const [comments, setComments] = useState<ResourceCommentViewModel[]>(resp?.comments);
  const [isLikeButton, setIsLikeButton] = useState<boolean>(resp.isLiked);

  const [isShareOpen, setIsShareOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();


  const headerProps: HeaderProps = {
    isLoggedIn: resp.isAuthenticated,    
    position: 'top_nav_individual_resource',
    customerName: resp.user?.name ?? '',
    customerImageUrl: resp.user?.profilePic ?? '',
    showManageEvaluations: true,
    isSubscribed: resp.user?.isSubscribed ?? false
  };

  const customerProfile: CustomerProfile = {
    name: resp.user?.name ?? '',
    profile_picture: resp.user?.profilePic ?? '',
  };

  useEffect(() => {
    const position = localStorage.getItem('position') ?? '';
    if (position !== 'reaction_books' && position !== 'book_summary') {
      mixpanelService.track('individual_resource_loads', {
        source: position,
      });
      localStorage.setItem('position', 'top_nav_individual_resource');
    }
  });

  useEffect(() => {
    // Track entry time when the component is mounted (user enters the page)
    const entryTime = Date.now();
    // Track exit time when the component is unmounted (user leaves the page)
    return () => {
      const position = localStorage.getItem('position') ?? '';

      if (position !== 'top_nav_individual_resource') {
        const exitTime = Date.now();
        const timeSpent = exitTime - entryTime;
        const timeInSeconds = timeSpent / 1000;
        mixpanelService.track('individual_resource_time_spend', {
          book: resp.data.data?.header,
          timeSpent: timeInSeconds,
        });
      }
      // localStorage.setItem("position", "book_summary");
    };
  }, []);

  useEffect(() => {
    const handleLikeToggle = async () => {
      try {
        const client = new BrowserCustomerClient();
        const response = await client?.GetResourceLikes(data?.resourceId ?? '');
        if (response?.success) {
          const data = response.data;
          setLikes(data);
        }
      } catch (error) {
        setIsCommentsPopupOpen(true);
      }
    };

    handleLikeToggle();
  }, [isLikeButton]);

  // If we have an error response, render the 404 component
  if (resp.error) {
    return <NotFound404 />;
  }

  const handleResourceLikeClick = async () => {
    if (!resp.isAuthenticated) {
      setIsCommentsPopupOpen(true);
      setIsLikeButton(false);
    } else {
      try {
        const client = new BrowserCustomerClient();
        const reqData: ResourceLikesInputModel = {
          resourceId: data?.resourceId ?? '',
        };
        const response = await client?.SaveResourceLike(reqData);
        if (response?.success) {
          setIsLikeButton(!isLikeButton);
        }
      } catch (error) {
        setIsCommentsPopupOpen(true);
      }
    }
  };

  const handleShareClick = () => {
    const pageLink = window.location.href;
    navigator.clipboard.writeText(pageLink); // Copy the link to clipboard
    setIsPopupLinkVisible(true); // Show the popup
    setIsShareOpen(!isShareOpen);
    setTimeout(() => setIsPopupLinkVisible(false), 3000); // Hide after 3 seconds
  };

  const handleSummaryPopupClose = () => {
    setIsSummaryPopupOpen(false);
  };

  // Function to toggle the post section visibility
  const handlePopupToggle = (commentId?: string) => {
    setIsReactionsPop(prev => !prev); // Toggle the state
    if (resp.isMobile) {
      navigate(`/knowledge-hub-book/${commentId}/reactions`);
    }
  };

  // Function to close the popup
  const handleReactionPopupClose = () => {
    setIsReactionsPop(false); // Close the popup
  };

  const handleReadSummary = async (resource_name: string, key: string) => {
    // console.log(resource_name);
    mixpanelService.track('read_summary_click', {
      resource_name: resource_name,
      location: 'individual_resources_page',
    });
    if (!resp.isAuthenticated) {
      setIsSummaryPopupOpen(true);
    } else {
      if (key !== '') {
        const client = new BrowserCustomerClient();
        const fileResp = await client.GetBucketFileUrl({
          key: key,
        });
		if(fileResp.success){
			// console.log('Book Summary: ', fileResp.data);
			window.open(fileResp.data, '_blank', 'noopener,noreferrer');
		}
        
      }
    }
  };

  const KhBookContextData: KnowledgeHubBookContextProps = {
    headerProps: headerProps,
    comments: comments,
    isPopupLinkVisible: isPopupLinkVisible,
    data: data,
    isCommentsPopupOpen: isCommentsPopupOpen,
    isSummaryPopupOpen: isSummaryPopupOpen,
    isReactionsPop: isReactionsPop,
    isOpen: isOpen,
    isLikeButton: isLikeButton,
    inputRef: inputRef,
    isLoggedIn: resp.isAuthenticated,
    customerProfile: customerProfile,
    likes: likes,    
  };

  return resp.error ? (
    <NotFound404 />
  ) : (
    <KhBookContext.Provider value={KhBookContextData}>
      {!resp.isMobile ? (
        <DesktopComponent
          setIsOpen={setIsOpen}
          handleReadSummary={handleReadSummary}
          setIsCommentsPopupOpen={setIsCommentsPopupOpen}
          handleResourceLikeClick={handleResourceLikeClick}
          handleShareClick={handleShareClick}
          setComments={setComments}
          handleSummaryPopupClose={handleSummaryPopupClose}
          handlePopupToggle={handlePopupToggle}
          handleReactionPopupClose={handleReactionPopupClose}
        />
      ) : (
        <MobileComponent
          setIsOpen={setIsOpen}
          handleReadSummary={handleReadSummary}
          setIsCommentsPopupOpen={setIsCommentsPopupOpen}
          handleResourceLikeClick={handleResourceLikeClick}
          handleShareClick={handleShareClick}
          setComments={setComments}
          handleSummaryPopupClose={handleSummaryPopupClose}
          handlePopupToggle={handlePopupToggle}
          handleReactionPopupClose={handleReactionPopupClose}
        />
      )}
    </KhBookContext.Provider>
  );
}

export const loader = async ({ context, params }: LoaderFunctionArgs) => {
  let likes: ResourceLikesViewModel = {
    noOfLikes: 0,
  };
  let comments: ResourceCommentViewModel[] = [];
  let isLiked: boolean = false;

  if (!params.id) return { error: true, ...context.clientContext, data: getCommonErrorMessage('Invalid book id'), likes, isLiked, comments };

  try {
    const service = context.serverContext.apiClient.customerService;

    const resp = await service.GetResource(params.id);
    // console.log('resp data:', resp);

    // Check if the resource ID matches the params ID
    if (resp.data.id !== params.id) {
      return {
        error: true,
        ...context.clientContext,
        data: getCommonErrorMessage('Invalid book id'),
        likes,
        isLiked,
        comments,
      };
    }
    const likesResp = service.GetResourceLikes(resp.data.resourceId);
    const commentsResp = service.GetResourceComments(resp.data.resourceId);
    const responses = await Promise.all([likesResp, commentsResp]);

    if (context.clientContext.isAuthenticated) {
      const isLikedResp = await service.IsLiked(resp.data.resourceId);
      isLiked = isLikedResp.data;
    }

    likes = responses[0].data;

    comments = responses[1].data;

    // console.log("Recevied books: ", resp.data);
    // console.log(resp.data);

    return { error: false, data: getApiData(resp.data), ...context.clientContext, likes, isLiked, comments };
  } catch (err) {
    console.log(err);
    if (err instanceof HFWApiError) {
      return { error: true, data: getApiError(err), ...context.clientContext, likes, isLiked, comments };
    } else {
      return { error: true, data: getCommonErrorMessage((err as Error).message), ...context.clientContext, likes, isLiked, comments };
    }
  }
};
